import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * DeleteGardenDialog — confirmation modal before deleting a garden.
 *
 * The user must type the exact garden name into the confirmation input
 * before the destructive action is enabled (#460). Comparison is
 * case-sensitive and does not trim whitespace.
 *
 * Props:
 *   gardenName  string          — shown in dialog body and required as confirmation
 *   onConfirm   () => Promise   — performs the delete; rejects on error
 *   onCancel    () => void
 *   isOpen      boolean
 */
const DeleteGardenDialog = ({ gardenName, onConfirm, onCancel, isOpen }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [wasOpen, setWasOpen] = useState(isOpen);
  const inputRef = useRef(null);

  const isConfirmed = confirmInput === gardenName;

  // Reset transient state when the dialog closes, during render rather than in
  // an effect (avoids a synchronous setState-in-effect cascade).
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setError(null);
      setIsDeleting(false);
      setConfirmInput('');
    }
  }

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isDeleting) onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isDeleting, onCancel]);

  const handleConfirm = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm();
    } catch {
      setError('Das Löschen hat nicht geklappt. Bitte versuche es erneut.');
      setIsDeleting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isDeleting) onCancel();
  };

  if (!isOpen) return null;

  return createPortal(
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- modal backdrop close-on-click; keyboard close via Cancel button */
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center"
      onClick={handleOverlayClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-garden-title"
        aria-describedby="delete-garden-desc"
        data-testid="delete-garden-dialog"
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
      >
        <h2 id="delete-garden-title" className="text-base font-semibold text-gray-900 mb-3">
          Garten löschen
        </h2>
        <p id="delete-garden-desc" className="text-sm text-gray-600 mb-5">
          Der Garten <strong>„{gardenName}&quot;</strong> wird aus deiner Übersicht entfernt.
          Das lässt sich später nicht mehr rückgängig machen.
        </p>
        <div className="mb-5">
          <label
            htmlFor="delete-garden-confirm-input"
            className="block text-sm text-gray-700 mb-2"
          >
            Zur Bestätigung den Garten-Namen eintippen:
          </label>
          <input
            id="delete-garden-confirm-input"
            ref={inputRef}
            type="text"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            disabled={isDeleting}
            placeholder="Garten-Name eintippen, um zu bestätigen"
            data-testid="delete-garden-name-input"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>
        {error && (
          <div className="mb-4 px-3 py-2 text-sm text-red-600 bg-red-50 rounded-md border border-red-100">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            data-testid="delete-garden-cancel-btn"
            className="px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isDeleting || !isConfirmed}
            data-testid="delete-garden-confirm-btn"
            className="px-4 py-3 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Wird gelöscht …' : 'Endgültig löschen'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeleteGardenDialog;
